import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListSubheader,
  Typography,
  Popper,
  ClickAwayListener,
  CircularProgress,
  Chip,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Description as DocumentIcon,
  Person as PersonIcon,
  Contacts as ContactIcon,
  Assignment as TaskIcon,
  Inventory2 as AssetIcon,
  MeetingRoom as DoorIcon,
  HelpOutline as HelpIcon,
  Link as LinkIcon,
  Apps as PageIcon,
  ConfirmationNumber as TicketIcon,
  EventNote as MeetingIcon,
  Article as NewsIcon,
  Chair as RoomIcon,
  HomeWork as BuildingIcon
} from '@mui/icons-material';
import searchService from '../services/searchService';

// Meilisearch wraps matched terms in these control characters instead of HTML
// tags (see server/services/meiliSearchService.js). Splitting on them means
// indexed content is never interpreted as markup.
const HL_PRE = '\u0001';
const HL_POST = '\u0002';
const HL_SPLIT = new RegExp(`[${HL_PRE}${HL_POST}]`);

const DEBOUNCE_MS = 180;
const MIN_QUERY_LENGTH = 2;

// Every `type` in server/config/searchSources.js needs an entry, or its results
// fall through to the "Other" group with a generic icon.
const TYPE_META = {
  page: { label: 'Pages', icon: <PageIcon /> },
  person: { label: 'People', icon: <PersonIcon /> },
  contact: { label: 'Contacts', icon: <ContactIcon /> },
  asset: { label: 'Assets', icon: <AssetIcon /> },
  help: { label: 'Help Articles', icon: <HelpIcon /> },
  ticket: { label: 'Tickets', icon: <TicketIcon /> },
  task: { label: 'Tasks', icon: <TaskIcon /> },
  meeting: { label: 'Meetings', icon: <MeetingIcon /> },
  door: { label: 'Doors', icon: <DoorIcon /> },
  news: { label: 'News', icon: <NewsIcon /> },
  link: { label: 'Shortcuts', icon: <LinkIcon /> },
  room: { label: 'Rooms', icon: <RoomIcon /> },
  building: { label: 'Buildings', icon: <BuildingIcon /> }
};

const typeLabel = (type) => TYPE_META[type]?.label || 'Other';
const typeIcon = (type) => TYPE_META[type]?.icon || <DocumentIcon />;

/** Render Meilisearch's marked-up text as <mark> runs without using innerHTML. */
const Highlighted = ({ text }) => {
  if (!text) return null;
  const parts = String(text).split(HL_SPLIT);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <Box key={i} component="mark" sx={{ bgcolor: 'warning.light', color: 'inherit', px: 0, borderRadius: 0.5 }}>
            {part}
          </Box>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
};

const SearchBar = ({ onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [facets, setFacets] = useState({});
  const [didYouMean, setDidYouMean] = useState(null);
  const [completions, setCompletions] = useState([]);
  const [typeFilter, setTypeFilter] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState(null);

  const searchInputRef = useRef(null);
  // Guards against a slow early request overwriting a newer response.
  const requestSeq = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (searchInputRef.current) searchInputRef.current.focus();
  }, []);

  const runSearch = useCallback(async (query, type) => {
    const seq = ++requestSeq.current;
    setIsSearching(true);
    setError(null);
    try {
      const data = await searchService.getSuggestions(query, { type });
      if (seq !== requestSeq.current) return; // a newer query already answered
      setResults(data.results || []);
      setFacets(data.facets || {});
      setDidYouMean(data.didYouMean || null);
      setCompletions(data.completions || []);
      setActiveIndex(0);
      setShowResults(true);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setResults([]);
      setDidYouMean(null);
      setCompletions([]);
      setError(err?.response?.status === 503 ? 'Search is unavailable right now' : 'Search failed');
      setShowResults(true);
    } finally {
      if (seq === requestSeq.current) setIsSearching(false);
    }
  }, []);

  // Debounce so a typed word is one request, not one per character.
  useEffect(() => {
    const query = searchQuery.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setFacets({});
      setDidYouMean(null);
      setCompletions([]);
      setShowResults(false);
      return undefined;
    }
    const timer = setTimeout(() => runSearch(query, typeFilter), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery, typeFilter, runSearch]);

  // Group for display while keeping a flat order for keyboard navigation.
  const groups = useMemo(() => {
    const byType = new Map();
    results.forEach((result) => {
      if (!byType.has(result.type)) byType.set(result.type, []);
      byType.get(result.type).push(result);
    });
    let flatIndex = 0;
    return [...byType.entries()].map(([type, items]) => ({
      type,
      items: items.map((item) => ({ ...item, flatIndex: flatIndex++ }))
    }));
  }, [results]);

  const closeResults = () => setShowResults(false);

  const openResult = (result) => {
    if (!result) return;
    navigate(result.url);
    setShowResults(false);
    setSearchQuery('');
    if (onClose) onClose();
  };

  const handleKeyDown = (event) => {
    if (!showResults || results.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (event.key === 'Enter') {
      // Open what is highlighted. Never guess at the first hit — that used to
      // navigate people somewhere they never chose.
      event.preventDefault();
      openResult(results[activeIndex]);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setResults([]);
    setFacets({});
    setDidYouMean(null);
    setCompletions([]);
    setTypeFilter(null);
    setShowResults(false);
  };

  /** Replace the word being typed with a completion, keeping earlier words. */
  const applyCompletion = (word) => {
    const parts = searchQuery.trim().split(/\s+/);
    parts[parts.length - 1] = word;
    setSearchQuery(parts.join(' ') + ' ');
    searchInputRef.current?.focus();
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') setShowResults(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const facetChips = Object.entries(facets).filter(([, count]) => count > 0);

  return (
    <ClickAwayListener onClickAway={closeResults}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <form onSubmit={(e) => e.preventDefault()}>
          <TextField
            inputRef={searchInputRef}
            placeholder="Search people, assets, doors, help..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => results.length > 0 && setShowResults(true)}
            variant="outlined"
            size="small"
            fullWidth
            autoComplete="off"
            sx={{
              backgroundColor: onClose ? 'transparent' : 'rgba(255, 255, 255, 0.15)',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                color: onClose ? 'inherit' : 'white',
                '& fieldset': {
                  borderColor: onClose ? 'rgba(0, 0, 0, 0.23)' : 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: onClose ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: onClose ? '#0066ff' : 'white',
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: onClose ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.7)',
                opacity: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: onClose ? 'inherit' : 'white' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {isSearching ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : searchQuery ? (
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      sx={{ color: onClose ? 'inherit' : 'white' }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </InputAdornment>
              ),
            }}
          />
        </form>

        <Popper
          open={showResults && (results.length > 0 || Boolean(error) || Boolean(searchQuery.trim()))}
          anchorEl={searchInputRef.current}
          placement="bottom-start"
          style={{ width: searchInputRef.current?.offsetWidth, zIndex: 1300 }}
        >
          <Paper elevation={3} sx={{ mt: 1, maxHeight: 460, overflow: 'auto' }}>
            {facetChips.length > 1 && (
              <Stack direction="row" spacing={0.5} sx={{ p: 1, flexWrap: 'wrap', gap: 0.5 }}>
                <Chip
                  size="small"
                  label="All"
                  color={typeFilter ? 'default' : 'primary'}
                  onClick={() => setTypeFilter(null)}
                />
                {facetChips.map(([type, count]) => (
                  <Chip
                    key={type}
                    size="small"
                    label={`${typeLabel(type)} (${count})`}
                    color={typeFilter === type ? 'primary' : 'default'}
                    onClick={() => setTypeFilter(typeFilter === type ? null : type)}
                  />
                ))}
              </Stack>
            )}

            {error && (
              <Typography variant="body2" color="error" sx={{ p: 2 }}>
                {error}
              </Typography>
            )}

            {!error && !isSearching && results.length === 0 && searchQuery.trim().length >= MIN_QUERY_LENGTH && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No results for “{searchQuery.trim()}”
                </Typography>
                {didYouMean && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Did you mean{' '}
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setSearchQuery(didYouMean)}
                      sx={{
                        background: 'none',
                        border: 'none',
                        p: 0,
                        font: 'inherit',
                        color: 'primary.main',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}
                    >
                      {didYouMean}
                    </Box>
                    ?
                  </Typography>
                )}
              </Box>
            )}

            {completions.length > 0 && (
              <Stack direction="row" spacing={0.5} sx={{ px: 1, pt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                {completions.map((word) => (
                  <Chip
                    key={word}
                    size="small"
                    variant="outlined"
                    label={word}
                    onClick={() => applyCompletion(word)}
                  />
                ))}
              </Stack>
            )}

            {groups.map((group) => (
              <List
                key={group.type}
                dense
                subheader={<ListSubheader disableSticky>{typeLabel(group.type)}</ListSubheader>}
              >
                {group.items.map((result) => (
                  <ListItem
                    button
                    key={result.id}
                    selected={result.flatIndex === activeIndex}
                    onMouseEnter={() => setActiveIndex(result.flatIndex)}
                    onClick={() => openResult(result)}
                  >
                    <ListItemIcon>{typeIcon(result.type)}</ListItemIcon>
                    <ListItemText
                      primary={<Highlighted text={result.title} />}
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={
                        <Typography
                          variant="body2"
                          component="div"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          <Highlighted text={result.description} />
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ))}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default SearchBar;
